import { FC, useEffect, useState } from 'react';
import { z } from 'zod';
import { Box, Button, ButtonBase, CircularProgress, Grid, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { Stack } from '@mui/system';
import { PersonaAvatarLarge } from '../PersonaAvatar';
import { PersonaDetailBio } from './PersonaDetailBio';
import { PersonaDetailQuote } from './PersonaDetailQuote';
import { PersonaDetailDescription } from './PersonaDetailDescription';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDrawerContext } from '../../drawer/DrawerContext';
import { useTypedPathParams } from '../../../../shared/fe/hooks/useTypedPathParams';
import { useApiQuery } from '../../query/hooks';
import { graphql } from '../../gql';
import { Add, EditOutlined, FileDownloadOutlined } from '@mui/icons-material';
import PersonaBlockTile from './PersonaBlockTile';
import { useModalContext } from '../../modal/ModalContext';
import { PersonaPopover } from '../PersonaPopover';
import JourneyBlockTile from './JourneyBlockTile';
import slugify from 'slugify';
import { useGeneratePDF } from '../../hooks/useGeneratePDF';

const personaDetail_getPersona = graphql(`
	query personaDetail_getPersona($id: UUID!) {
		getPersona(by: { id: $id }) {
			age
			description
			gender
			location
			id
			job
			name
			quote
			status
			color
			journeys {
				id
			}
			blocks(orderBy: { order: asc }) {
				id
				...personaBlockTile_blockFragment
			}
			...PersonaAvatar_persona
			...personaPopover_personaFragment
		}
	}
`);

const schema = z.object({
	id: z.string(),
});

export const PersonaDetailPage: FC = () => {
	const theme = useTheme();
	const intl = useIntl();
	const { id } = useTypedPathParams(schema);
	const { setDrawerWithoutPrevParams } = useDrawerContext();
	const { data: persona, isLoading } = useApiQuery(personaDetail_getPersona, { variables: { id } });
	const { setModal } = useModalContext();
	const [showEditIcon, setShowEditIcon] = useState<boolean>(false);
	const { setPdfExportData, generatePdf, isExporting } = useGeneratePDF();

	useEffect(() => {
		setPdfExportData({
			elementId: 'persona-detail',
			fileName: `${slugify(persona?.getPersona?.name ?? 'persona')}_detail`,
			orientation: 'landscape',
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (isLoading) {
		return (
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
				<CircularProgress />
			</Box>
		);
	}

	if (persona?.getPersona == null) {
		return null;
	}

	const { getPersona } = persona;

	return (
		<Box
			padding={3}
			paddingBottom={4}
			height={'100%'}
			position={'relative'}
			overflow={{ xs: 'auto', sm: 'hidden' }}
			id={'persona-detail'}
			data-apply-visible-overflow
		>
			<Stack direction={'row'} gap={1}>
				<Typography variant={'h3'} fontWeight={'700'}>
					{getPersona.name}
				</Typography>
				<PersonaPopover personaData={getPersona} />
				<Button
					data-ignore-pdf
					variant={'text'}
					sx={{
						marginLeft: 'auto',
						color: theme.palette.text.secondary,
						'&:hover': { background: 'none', color: 'inherit' },
					}}
					startIcon={<FileDownloadOutlined />}
					onClick={async () => await generatePdf()}
				>
					<FormattedMessage id={'journey.view.header.export'} />
				</Button>
			</Stack>
			<Grid container height={'100%'}>
				<Grid
					item
					xs={12}
					sm={6}
					md={4}
					xl={3}
					height={{ sm: '100%' }}
					maxWidth={
						isExporting
							? 350
							: {
									xl: 350,
								}
					}
					paddingTop={3}
					paddingBottom={isExporting ? 3 : { sx: 0, sm: 3 }}
					paddingRight={isExporting ? 3 : { sx: 0, sm: 3 }}
				>
					<Stack
						alignItems={'center'}
						height={'100%'}
						width={'100%'}
						gap={4}
						padding={2.5}
						overflow={isExporting ? 'auto' : { sm: 'auto' }}
						sx={{ backgroundColor: theme.palette.grey[50], borderRadius: '8px' }}
						onMouseEnter={() => setShowEditIcon(true)}
						onMouseLeave={() => setShowEditIcon(false)}
					>
						<Stack width={'100%'} alignItems={'center'} gap={2}>
							<Stack direction={'row'} width={'100%'} alignItems={'center'} justifyContent={'end'}>
								<Tooltip title={intl.formatMessage({ id: 'common.edit' })}>
									<IconButton
										color={'primary'}
										onClick={() => setDrawerWithoutPrevParams('editPersonaDetail', { personaId: getPersona.id })}
									>
										<EditOutlined htmlColor={theme.palette.grey[500]} sx={{ opacity: showEditIcon ? 1 : 0 }} />
									</IconButton>
								</Tooltip>
							</Stack>
							<PersonaAvatarLarge persona={getPersona} color={getPersona.color} withBorder />
						</Stack>
						<PersonaDetailQuote value={getPersona.quote} />
						<PersonaDetailDescription value={getPersona.description} />
						<Stack justifyContent={'center'} alignItems={'center'} width={'100%'} gap={1}>
							<PersonaDetailBio label={intl.formatMessage({ id: 'persona.detail.bio.age' })} value={getPersona.age} />
							{getPersona.gender && (
								<PersonaDetailBio
									label={intl.formatMessage({ id: 'persona.detail.bio.gender' })}
									value={intl.formatMessage({ id: `persona.detail.bio.gender-${getPersona.gender}` })}
								/>
							)}
							<PersonaDetailBio label={intl.formatMessage({ id: 'persona.detail.bio.job' })} value={getPersona.job} />
							{getPersona.status && (
								<PersonaDetailBio
									label={intl.formatMessage({ id: 'persona.detail.bio.status' })}
									value={intl.formatMessage({ id: `persona.detail.bio.status-${getPersona.status}` })}
								/>
							)}
							<PersonaDetailBio label={intl.formatMessage({ id: 'persona.detail.bio.location' })} value={getPersona.location} />
						</Stack>
					</Stack>
				</Grid>
				<Grid
					item
					sm={6}
					md={8}
					xl={9}
					maxWidth={isExporting ? 'unset' : { xl: 'unset' }}
					flex={
						isExporting
							? 1
							: {
									xl: 1,
								}
					}
					height={'100%'}
					overflow={{ sm: 'auto' }}
					marginTop={3}
					paddingBottom={6}
				>
					<Grid
						item
						display={'grid'}
						gap={3}
						marginBottom={getPersona.blocks.length > 0 ? 3 : 0}
						gridTemplateColumns={
							isExporting ? 'repeat(2, 1fr)' : { xs: 'repeat(1, 1fr)', sm: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)', lg: 'repeat(2, 1fr)' }
						}
					>
						{getPersona.blocks.map((block) => (
							<PersonaBlockTile blockFragment={block} personaId={getPersona.id} key={block.id} />
						))}
					</Grid>
					<ButtonBase
						onClick={() =>
							setModal({
								type: 'createPersonaBlock',
								payload: { personaId: getPersona.id, amountOfBlocks: getPersona.blocks.length },
								maxWidth: 'sm',
							})
						}
						data-ignore-pdf
						sx={{ width: '100%', marginBottom: 4 }}
					>
						<Box
							display={'flex'}
							width={'100%'}
							justifyContent={'center'}
							alignItems={'center'}
							gap={1}
							paddingY={1}
							borderRadius={1}
							sx={{
								backgroundColor: theme.palette.grey[50],
								'&:hover': {
									backgroundColor: theme.palette.grey[100],
								},
							}}
						>
							<Add sx={{ width: '18px', height: '18px', color: theme.palette.grey[500] }} />
							<Typography variant={'caption'} color={theme.palette.grey[500]}>
								<FormattedMessage id={'persona.detail.block.create'} />
							</Typography>
						</Box>
					</ButtonBase>
					<Stack flexDirection={'row'} alignItems={'center'} gap={1} marginBottom={2}>
						<Typography variant={'h5'} fontWeight={'700'}>
							<FormattedMessage id={'persona.detail.journeys.title'} />
						</Typography>
						<Box color={theme.palette.grey[500]} fontSize={'14px'} mt={0.2}>
							{getPersona.journeys?.length}
						</Box>
					</Stack>
					{getPersona.journeys && getPersona.journeys.length > 0 && (
						<Grid item display={'grid'} gap={1} lg={12} marginBottom={3}>
							{getPersona.journeys.map((journeyId) => (
								<JourneyBlockTile journeyId={journeyId.id} personaId={getPersona.id} key={journeyId.id} />
							))}
						</Grid>
					)}
				</Grid>
			</Grid>
		</Box>
	);
};
